import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import WriteButton from "@/components/WriteButton";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/pos-api";
import { rupiah } from "@/lib/format";
import { toast } from "sonner";
import { Search, UserPlus, User, X } from "lucide-react";

export default function CustomerDialog({ open, onClose, onSelect, selected }) {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [creating, setCreating] = useState(false);
  const searchRef = useRef(null);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const listRef = useRef(null);
  const pendingArrow = useRef(null);

  useEffect(() => {
    if (!open) return;
    setQ(""); setName(""); setPhone(""); setCreating(false);
    setActive(-1); pendingArrow.current = null;
    const timer = setTimeout(() => searchRef.current?.focus(), 60);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let current = true;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/customers", { params: q ? { q } : {} });
        if (current) { setList(data); setActive(-1); }
      } catch (e) {
        if (current) { setList([]); toast.error(apiError(e)); }
      } finally { if (current) setLoading(false); }
    }, 250);
    return () => { current = false; clearTimeout(timer); };
  }, [q, open]);

  useEffect(() => {
    if (!open || loading || pendingArrow.current === null) return;
    const index = Math.min(pendingArrow.current, list.length - 1);
    pendingArrow.current = null;
    if (index < 0) return;
    setActive(index);
    listRef.current?.querySelector(`[data-nav-index="${index}"]`)?.focus();
  }, [list, loading, open]);

  const searchChanged = (value) => {
    setQ(value); setActive(-1); setLoading(true); setList([]);
    pendingArrow.current = null;
  };
  const choose = (customer) => { onSelect(customer); onClose(); };
  const navigate = (event, index = -1) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault(); event.stopPropagation();
      if (loading) {
        pendingArrow.current = Math.max(0, (pendingArrow.current ?? -1) + (event.key === "ArrowDown" ? 1 : -1));
        return;
      }
      if (!list.length) return;
      const next = event.key === "ArrowDown" ? Math.min(list.length - 1, index + 1) : index - 1;
      if (next < 0) { setActive(-1); searchRef.current?.focus(); }
      else { setActive(next); listRef.current?.querySelector(`[data-nav-index="${next}"]`)?.focus(); }
    } else if (event.key === "Enter" && index >= 0 && !loading) {
      event.preventDefault(); event.stopPropagation(); choose(list[index]);
    }
  };

  const create = async () => {
    if (!name.trim()) { toast.error("Nama pelanggan wajib diisi"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/customers", { name: name.trim(), phone: phone.trim() || null });
      toast.success("Pelanggan ditambahkan");
      onSelect(data);
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="customer-dialog" className="bg-ui-card border-ui-line text-ui-text max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Pelanggan (F3)</DialogTitle>
          <DialogDescription className="text-ui-muted text-xs">Ketik nama, tekan ↓ untuk memilih hasil, lalu Enter.</DialogDescription>
        </DialogHeader>

        {selected && (
          <div className="flex items-center gap-2 rounded-lg bg-brand-soft border border-brand/30 px-3 py-2 text-sm">
            <User className="h-4 w-4 text-brand-readable" />
            <span className="flex-1 truncate">Terpilih: <b>{selected.name}</b></span>
            <button data-testid="customer-clear-btn" onClick={() => { onSelect(null); }} className="text-ui-muted hover:text-danger-readable">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ui-muted" />
          <Input
            ref={searchRef}
            data-testid="customer-search-input"
            value={q}
            onChange={(e) => searchChanged(e.target.value)}
            onKeyDown={(e) => navigate(e)}
            placeholder="Cari nama / no. HP…"
            className="h-11 pl-10 bg-ui-card border-ui-line"
          />
        </div>

        <div ref={listRef} data-testid="customer-list" aria-busy={loading} className="max-h-52 overflow-y-auto space-y-1">
          {loading ? <div role="status" className="text-center text-ui-muted text-sm py-6">Mencari pelanggan…</div> : list.length === 0 ? (
            <div className="text-center text-ui-muted text-sm py-6">Tidak ada pelanggan</div>
          ) : (
            list.map((c, idx) => (
              <button
                key={c.id}
                data-testid={`customer-item-${c.id}`}
                data-nav-index={idx}
                onFocus={() => setActive(idx)}
                onKeyDown={(e) => navigate(e, idx)}
                onClick={() => choose(c)}
                className={`w-full flex items-center justify-between gap-2 rounded-lg border bg-ui-soft px-3 py-2 text-left no-select ${idx === active ? "border-brand ring-1 ring-brand/60" : "border-ui-line hover:border-brand/60"}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                  {c.phone ? <div className="text-[11px] text-ui-muted">{c.phone}</div> : null}
                </div>
                {c.debt > 0 ? <span className="text-[11px] font-mono text-danger-readable shrink-0">Hutang {rupiah(c.debt)}</span> : null}
              </button>
            ))
          )}
        </div>

        <div className="border-t border-ui-line pt-3 space-y-2">
          <div className="text-xs text-ui-muted flex items-center gap-1.5"><UserPlus className="h-3.5 w-3.5" /> Tambah pelanggan baru</div>
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="new-customer-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nama" className="h-10 bg-ui-card border-ui-line" />
            <Input data-testid="new-customer-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="No. HP (opsional)" className="h-10 bg-ui-card border-ui-line" />
          </div>
          <WriteButton data-testid="create-customer-btn" onClick={create} disabled={creating} className="w-full h-10 bg-brand-button hover:bg-brand-button text-ui-text font-bold gap-2">
            <UserPlus className="h-4 w-4" /> Tambah & Pilih
          </WriteButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}
